import apiClient from './client';

export const reportsAPI = {
  // Campaign reports
  getCampaignSummary: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/summary`, { params }),

  getCampaignCreators: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/creators`, { params }),

  getCampaignTimeline: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/timeline`, { params }),

  getCampaignPlatforms: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/platforms`, { params }),

  getCampaignContent: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/content`, { params }),

  exportCampaignData: (campaignId, params) =>
    apiClient.get(`/reports/campaign/${campaignId}/export`, { params }),

  // Creator comparison
  compareCreators: (creatorIds, params) =>
    apiClient.get('/reports/compare/creators', {
      params: { ...params, creatorIds: creatorIds.join(',') }
    }),

  // Game analytics
  getGameAnalytics: (gameId, params) =>
    apiClient.get(`/reports/game/${gameId}/analytics`, { params }),

  // Generate PDF report (server-side). PDF pipeline (TikTok oEmbed
  // fetches + Puppeteer render) routinely takes 30–90s, so override the
  // 10s global axios timeout to 5 minutes — otherwise the browser aborts
  // and shows "Failed to generate PDF" even though the server succeeded.
  generatePDF: async (data) => {
    const response = await apiClient.post('/reports/generate-pdf', data, {
      responseType: 'blob',
      timeout: 5 * 60 * 1000,
    });
    return response;
  },
};

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

// Format large numbers with K, M, B suffixes
export const formatNumber = (num, decimals = 1) => {
  if (!num || num === 0) return '0';
  
  const absNum = Math.abs(num);
  
  if (absNum >= 1e9) {
    return (num / 1e9).toFixed(decimals) + 'B';
  }
  if (absNum >= 1e6) {
    return (num / 1e6).toFixed(decimals) + 'M';
  }
  if (absNum >= 1e3) {
    return (num / 1e3).toFixed(decimals) + 'K';
  }
  
  return num.toLocaleString();
};

// Format as full number with commas
export const formatFullNumber = (num) => {
  if (!num || num === 0) return '0';
  return num.toLocaleString();
};

// Format currency
export const formatCurrency = (amount, currency = 'GBP') => {
  if (!amount || amount === 0) return '£0';
  
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(amount);
};

// Format percentage
export const formatPercent = (value, decimals = 1) => {
  if (!value || value === 0) return '0%';
  return `${value.toFixed(decimals)}%`;
};

// Format engagement rate
export const formatEngagementRate = (rate) => {
  if (!rate || rate === 0) return '0%';
  return `${rate.toFixed(2)}%`;
};

// Format watch hours
export const formatWatchHours = (hours) => {
  if (!hours || hours === 0) return '0h';
  
  if (hours >= 1000) {
    return `${(hours / 1000).toFixed(1)}K hrs`;
  }
  
  return `${Math.round(hours)} hrs`;
};

// Format duration (minutes to readable)
export const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  
  return `${mins}m`;
};

// Format date
export const formatDate = (date, format = 'short') => {
  if (!date) return '-';
  
  const d = new Date(date);
  
  if (format === 'short') {
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
  
  if (format === 'long') {
    return d.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }
  
  if (format === 'relative') {
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  }
  
  return d.toLocaleDateString('en-GB');
};

// Format date range
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate, 'short');
  const end = formatDate(endDate, 'short');
  return `${start} - ${end}`;
};

// Get platform color
export const getPlatformColor = (platform) => {
  const colors = {
    twitch: '#9146FF',
    youtube: '#FF0000',
    tiktok: '#000000',
    twitter: '#1DA1F2',
    instagram: '#E4405F',
    facebook: '#1877F2',
    kick: '#00E70A'
  };
  
  return colors[platform?.toLowerCase()] || '#6B7280';
};

// Get platform icon name (for lucide-react)
export const getPlatformIcon = (platform) => {
  const icons = {
    twitch: 'Twitch',
    kick: 'Radio',
    youtube: 'Youtube',
    tiktok: 'Music2',
    twitter: 'Twitter',
    instagram: 'Instagram',
    facebook: 'Facebook'
  };
  
  return icons[platform?.toLowerCase()] || 'Globe';
};

// Get tier color
export const getTierColor = (tier) => {
  const colors = {
    mega: '#FF6B6B',
    macro: '#4ECDC4',
    mid: '#45B7D1',
    micro: '#96CEB4',
    nano: '#FFEAA7'
  };
  
  return colors[tier?.toLowerCase()] || '#6B7280';
};

// Get status color
export const getStatusColor = (status) => {
  const colors = {
    active: '#10B981',
    completed: '#3B82F6',
    planning: '#F59E0B',
    paused: '#6B7280',
    cancelled: '#EF4444',
    invited: '#8B5CF6',
    confirmed: '#10B981',
    declined: '#EF4444',
    pending: '#F59E0B',
    paid: '#10B981'
  };
  
  return colors[status?.toLowerCase()] || '#6B7280';
};

// Calculate percentage change
export const calculateChange = (current, previous) => {
  if (!previous || previous === 0) return null;
  return ((current - previous) / previous) * 100;
};

// Format change with arrow
export const formatChange = (change) => {
  if (change === null || change === undefined) return null;
  
  const arrow = change >= 0 ? '↑' : '↓';
  const color = change >= 0 ? 'text-green-500' : 'text-red-500';
  
  return {
    value: `${arrow} ${Math.abs(change).toFixed(1)}%`,
    color,
    isPositive: change >= 0
  };
};

// Generate chart colors
export const generateChartColors = (count) => {
  const baseColors = [
    '#6366F1', // Indigo
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#F43F5E', // Rose
    '#F97316', // Orange
    '#EAB308', // Yellow
    '#22C55E', // Green
    '#14B8A6', // Teal
    '#06B6D4', // Cyan
    '#3B82F6', // Blue
  ];
  
  if (count <= baseColors.length) {
    return baseColors.slice(0, count);
  }
  
  // Generate more colors if needed
  const colors = [...baseColors];
  while (colors.length < count) {
    const hue = (colors.length * 137.5) % 360;
    colors.push(`hsl(${hue}, 70%, 50%)`);
  }
  
  return colors;
};

// Truncate text
export const truncateText = (text, maxLength = 50) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export default reportsAPI;
